import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://ninjathlete.com';

// Inject or update a <link> tag in <head>
const setLink = (rel, href, extra = {}) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};

// Inject or update a <meta> tag in <head>
const setMeta = (attr, attrVal, content) => {
  let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, attrVal);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
};

// Inject a JSON-LD script in <head>, return cleanup function
const injectJsonLd = (id, data) => {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
  return script;
};

// Canonical URL hook — injects <link rel="canonical"> on every route change
// Never removes it — just updates href — so there is always a canonical tag in <head>
export const useCanonicalUrl = () => {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, '') || '/';
    setLink('canonical', `${SITE_URL}${path}`);
    // No cleanup: updating href on each route change is correct; removing causes a brief gap
  }, [location.pathname]);
};

// Trust/schema audit: organizations and fabricated panels must never appear
// as reviewedBy — reviewer attribution requires an explicit real review record.
const ORG_NAME_RE = /library|pubmed|society|journal|institute|university|press|network|mdpi|frontiers|jama|cell|fda|nih|clinic|association|medicine|endocrine|panel|team|editorial/i;

// Article SEO — Pure DOM injection (bypasses react-helmet-async v2 which crashes with <script>/<title> children)
export const ArticleJsonLd = ({ article, slug }) => {
  useEffect(() => {
    if (!article) return;

    const description = article.meta_description || article.excerpt || '';
    const baseTitle = article.meta_title || article.title || '';
    // Avoid double-suffix when meta_title already ends with " | NinjAthlete"
    const pageTitle = /\|\s*NinjAthlete\s*$/i.test(baseTitle)
      ? baseTitle
      : `${baseTitle} | NinjAthlete`;

    // Set page title
    document.title = pageTitle;

    // Set/update meta tags
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', article.title || '');
    setMeta('property', 'og:description', article.excerpt || '');
    const ogImage = article.featured_image
      ? (article.featured_image.startsWith('http') ? article.featured_image : `${SITE_URL}${article.featured_image}`)
      : '';
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:type', 'article');
    setMeta('property', 'og:url', `${SITE_URL}/blogs/article/${slug}`);

    // Canonical URL — honor an explicit canonical_url override (used to
    // consolidate ranking signals onto another URL, e.g. the interactive report).
    const canonicalHref = article.canonical_url
      ? (article.canonical_url.startsWith('http') ? article.canonical_url : `${SITE_URL}${article.canonical_url}`)
      : `${SITE_URL}/blogs/article/${slug}`;
    const canonicalEl = setLink('canonical', canonicalHref);

    // Robots — noindex de-prioritized/pruned pages (keeps them crawlable via follow)
    setMeta('name', 'robots', article.noindex ? 'noindex, follow' : 'index, follow');

    // Enhanced JSON-LD schema with SpeakableSpecification for AEO/GEO
    const isPeptideArticle = ['peptides', 'peptide-therapy', 'biohacking', 'longevity', 'supplements'].includes(article.category?.toLowerCase())
      || (article.tags || []).some(t => ['peptide', 'bpc-157', 'aod-9604', 'dihexa', 'ghk-cu', 'mk-677', 'retatrutide', 'tb-500', 'mots-c', 'kpv', 'semaglutide'].includes(t?.toLowerCase()))
      || /peptide|bpc.?157|aod.?9604|dihexa|ghk.?cu|mk.?677|retatrutide|tb.?500|semaglutide/i.test(article.title || '');

    const authorSchema = {
      "@type": "Organization",
      "name": "NinjAthlete",
      "url": SITE_URL,
      "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo.png` },
      "sameAs": ["https://instagram.com/ninjathlete", "https://twitter.com/ninjathlete"]
    };

    const publisherSchema = {
      "@type": "Organization",
      "name": "NinjAthlete",
      "url": SITE_URL,
      "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo.png` },
      "sameAs": ["https://instagram.com/ninjathlete", "https://twitter.com/ninjathlete"]
    };

    const schema = article.schema || {
      "@context": "https://schema.org",
      "@type": isPeptideArticle ? "MedicalWebPage" : "Article",
      "headline": article.title,
      "name": article.title,
      "description": description,
      "image": ogImage,
      "datePublished": article.created_at,
      "dateModified": article.updated_at || article.created_at,
      ...(isPeptideArticle && article.last_reviewed ? { "lastReviewed": article.last_reviewed } : {}),
      ...(article.reviewed_by && !ORG_NAME_RE.test(article.reviewed_by) ? {
        "reviewedBy": { "@type": "Person", "name": article.reviewed_by },
      } : {}),
      ...(isPeptideArticle ? { "medicalAudience": { "@type": "MedicalAudience", "audienceType": "Patient" } } : {}),
      ...(isPeptideArticle ? { "specialty": { "@type": "MedicalSpecialty", "name": "Endocrinology" } } : {}),
      "author": authorSchema,
      "publisher": publisherSchema,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${SITE_URL}/blogs/article/${slug}`
      },
      "articleSection": article.category,
      "keywords": (article.seo_keywords || article.tags || []).join(', '),
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["[data-speakable]", ".direct-answer", ".article-content h1", ".article-content h2"]
      },
      "isAccessibleForFree": true,
      "inLanguage": "en-US",
      ...(Array.isArray(article.citations) && article.citations.length ? {
        "citation": article.citations.map(c => ({
          "@type": isPeptideArticle ? "MedicalScholarlyArticle" : "ScholarlyArticle",
          "headline": c.title,
          "author": (c.authors || []).map(n => ({ "@type": "Person", "name": n })),
          ...(c.year ? { "datePublished": String(c.year) } : {}),
          ...(c.journal ? { "isPartOf": { "@type": "Periodical", "name": c.journal } } : {}),
          "sameAs": c.url,
          "identifier": { "@type": "PropertyValue", "propertyID": "PMID", "value": c.pmid }
        }))
      } : {}),
      ...(article.aggregate_rating ? {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": article.aggregate_rating.rating_value,
          "reviewCount": article.aggregate_rating.review_count,
          "bestRating": article.aggregate_rating.best_rating || 5,
          "worstRating": article.aggregate_rating.worst_rating || 1
        }
      } : {})
    };
    // Trust/schema audit: reviewer attribution requires an explicit real
    // review record. Source organizations are citations, never reviewers,
    // and no default reviewer or review date is ever fabricated.
    const isFakeReviewer = (rv) => !rv
      || Array.isArray(rv)
      || rv['@type'] === 'Organization'
      || ORG_NAME_RE.test(rv.name || '')
      || (rv.name || '').includes('Panel');
    if (schema.reviewedBy && isFakeReviewer(schema.reviewedBy)) {
      delete schema.reviewedBy;
    }
    if (schema.lastReviewed && !article.last_reviewed && !article.schema) {
      delete schema.lastReviewed;
    }
    if (Array.isArray(schema['@graph'])) {
      schema['@graph'].forEach((n) => {
        if (n && n.reviewedBy && isFakeReviewer(n.reviewedBy)) delete n.reviewedBy;
      });
    }
    const schemaScript = injectJsonLd('article-schema-jsonld', schema);

    // Curated @graph schemas (e.g. the HYROX guide) already carry their own
    // BreadcrumbList / FAQPage nodes — don't inject duplicates.
    const graphTypes = new Set(
      Array.isArray(schema['@graph']) ? schema['@graph'].map((n) => n && n['@type']) : []
    );

    // Breadcrumb schema
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${SITE_URL}/blog` },
        ...(article.category ? [{ "@type": "ListItem", "position": 3, "name": article.category, "item": `${SITE_URL}/blog?category=${encodeURIComponent(article.category)}` }] : []),
        { "@type": "ListItem", "position": article.category ? 4 : 3, "name": article.title, "item": `${SITE_URL}/blogs/article/${slug}` }
      ]
    };
    const breadcrumbScript = graphTypes.has('BreadcrumbList') ? null : injectJsonLd('breadcrumb-schema-jsonld', breadcrumb);

    // SoftwareApplication-style schema for interactive tools embedded in articles
    const injected = [schemaScript, breadcrumbScript].filter(Boolean);

    // FAQPage schema — extracted from article FAQ data or content
    if (article.faqs && article.faqs.length > 0 && !graphTypes.has('FAQPage')) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": article.faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question || faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.answer || faq.a }
        }))
      };
      injected.push(injectJsonLd('article-faq-schema-jsonld', faqSchema));
    }

    if (article.interactive_components || article.has_calculator || article.category === 'Tools') {
      const appSchema = {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": `${article.title} - Interactive Tool`,
        "url": `${SITE_URL}/blogs/article/${slug}`,
        "applicationCategory": "HealthApplication",
        "operatingSystem": "Web Browser",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
        "author": { "@type": "Organization", "name": "NinjAthlete", "url": SITE_URL }
      };
      injected.push(injectJsonLd('article-app-schema-jsonld', appSchema));
    }

    return () => {
      injected.forEach(s => { if (s?.parentNode) s.parentNode.removeChild(s); });
      // Do NOT remove canonical — let useCanonicalUrl manage it persistently
      document.title = 'NinjAthlete | Biohacking, Peptide Therapy & Performance Optimization';
    };
  }, [article, slug]);

  return null;
};

// Product SEO — Full Product schema with Google Shopping / Merchant Center attributes
export const ProductSchema = ({ product, reviewStats, reviews }) => {
  useEffect(() => {
    if (!product) return;
    const seoTitle = product.seo_title || product.name;
    const seoDesc = product.seo_description || product.description || '';
    document.title = `${seoTitle} | NinjAthlete Shop`;
    setMeta('name', 'description', seoDesc);
    setMeta('property', 'og:title', seoTitle);
    setMeta('property', 'og:image', product.images?.[0] || '');
    setMeta('property', 'og:type', 'product');
    setMeta('property', 'og:url', `${SITE_URL}/shop/${product.slug}`);
    setMeta('property', 'product:price:amount', product.price?.toString() || '');
    setMeta('property', 'product:price:currency', 'USD');
    if (product.colors?.length) {
      setMeta('property', 'product:color', product.colors.join(', '));
    }
    if (product.target_gender) {
      setMeta('property', 'product:target_gender', product.target_gender);
    }

    const priceValidUntil = new Date(Date.now() + 90 * 24 * 3600 * 1000).toISOString().split('T')[0];
    // Resolve per-product return policy (mirrors backend returns_policy.py logic).
    // Priority: explicit product.returns_policy → digital category auto-detect → 30-day-free default.
    const DIGITAL_CATS = new Set(['guide','guides','ebook','ebooks','pdf','download','downloads','digital','course','courses','training plan','training plans','subscription','membership','license']);
    const DIGITAL_TAGS = new Set(['digital','pdf','download','ebook','course','subscription']);
    const cat = (product.category || '').toLowerCase();
    const ptype = (product.product_type || '').toLowerCase();
    const tagSet = new Set((product.tags || []).map(t => String(t).toLowerCase()));
    let policyKey = (product.returns_policy || '').toLowerCase();
    if (!['30day_free','14day_paid','final_sale','final_sale_digital'].includes(policyKey)) {
      if (DIGITAL_CATS.has(cat) || DIGITAL_CATS.has(ptype) || [...DIGITAL_TAGS].some(t => tagSet.has(t))) {
        policyKey = 'final_sale_digital';
      } else {
        policyKey = '30day_free';
      }
    }
    const merchantReturnPolicy = (() => {
      const base = { "@type": "MerchantReturnPolicy", "applicableCountry": "US" };
      if (policyKey === '30day_free') return { ...base, "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow", "merchantReturnDays": 30, "returnMethod": "https://schema.org/ReturnByMail", "returnFees": "https://schema.org/FreeReturn" };
      if (policyKey === '14day_paid') return { ...base, "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow", "merchantReturnDays": 14, "returnMethod": "https://schema.org/ReturnByMail", "returnFees": "https://schema.org/ReturnShippingFees" };
      return { ...base, "returnPolicyCategory": "https://schema.org/MerchantReturnNotPermitted" };
    })();
    const shippingDetails = {
      "@type": "OfferShippingDetails",
      "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "US" },
      "shippingRate": { "@type": "MonetaryAmount", "value": "5.99", "currency": "USD" },
      "deliveryTime": { "@type": "ShippingDeliveryTime", "handlingTime": { "@type": "QuantitativeValue", "minValue": 1, "maxValue": 3, "unitCode": "d" }, "transitTime": { "@type": "QuantitativeValue", "minValue": 3, "maxValue": 7, "unitCode": "d" } },
    };

    // Build rich Product JSON-LD with Google Shopping required fields
    const offers = (product.variants && product.variants.length > 0)
      ? product.variants.map(v => ({
          "@type": "Offer",
          "name": v.title || product.name,
          "price": v.price || product.price || 0,
          "priceCurrency": "USD",
          "availability": (product.inventory_count > 0) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "url": `${SITE_URL}/shop/${product.slug}`,
          "seller": { "@type": "Organization", "name": "NinjAthlete" },
          "priceValidUntil": priceValidUntil,
          "itemCondition": "https://schema.org/NewCondition",
          "shippingDetails": shippingDetails,
          "hasMerchantReturnPolicy": merchantReturnPolicy,
          ...(v.option1 && v.option1 !== "Default Title" ? { "color": v.option1 } : {}),
        }))
      : [{
          "@type": "Offer",
          "price": product.price || 0,
          "priceCurrency": "USD",
          "availability": (product.inventory_count > 0) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "url": `${SITE_URL}/shop/${product.slug}`,
          "seller": { "@type": "Organization", "name": "NinjAthlete" },
          "priceValidUntil": priceValidUntil,
          "itemCondition": "https://schema.org/NewCondition",
          "shippingDetails": shippingDetails,
          "hasMerchantReturnPolicy": merchantReturnPolicy,
        }];

    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "description": seoDesc,
      "image": product.images || [],
      "url": `${SITE_URL}/shop/${product.slug}`,
      "sku": product.mpn || product.slug,
      "mpn": product.mpn || product.slug,
      "brand": { "@type": "Brand", "name": "NinjAthlete" },
      "category": product.google_product_category_name || product.category || "Athletic Apparel",
      "itemCondition": "https://schema.org/NewCondition",
      ...(product.colors?.length ? { "color": product.colors.join(' / ') } : {}),
      ...(product.material ? { "material": product.material } : {}),
      ...(product.target_gender ? { "audience": { "@type": "PeopleAudience", "suggestedGender": product.target_gender } } : {}),
      ...(product.age_group ? { "audience": { "@type": "PeopleAudience", "suggestedGender": product.target_gender || "unisex", "suggestedAge": product.age_group } } : {}),
      "offers": offers.length === 1 ? offers[0] : { "@type": "AggregateOffer", "lowPrice": Math.min(...offers.map(o => o.price)), "highPrice": Math.max(...offers.map(o => o.price)), "priceCurrency": "USD", "offerCount": offers.length, "offers": offers },
      "hasMerchantReturnPolicy": merchantReturnPolicy,
      ...(reviewStats && reviewStats.total > 0 ? {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": reviewStats.average_rating,
          "reviewCount": reviewStats.total,
          "bestRating": 5,
          "worstRating": 1
        },
        ...((reviews || []).length > 0 ? {
          "review": reviews.slice(0, 5).map(r => ({
            "@type": "Review",
            "reviewRating": { "@type": "Rating", "ratingValue": r.rating, "bestRating": 5, "worstRating": 1 },
            "author": { "@type": "Person", "name": r.user_name || "Verified Customer" },
            ...(r.created_at ? { "datePublished": String(r.created_at).slice(0, 10) } : {}),
            ...(r.title ? { "name": r.title } : {}),
            "reviewBody": r.review_text || "",
          })),
        } : {}),
      } : (product.aggregate_rating ? {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": product.aggregate_rating.rating_value || 4.5,
          "reviewCount": product.aggregate_rating.review_count || 1,
          "bestRating": 5,
          "worstRating": 1
        }
      } : {})),
    };
    const schemaScript = injectJsonLd('product-schema-jsonld', schema);

    return () => {
      if (schemaScript?.parentNode) schemaScript.parentNode.removeChild(schemaScript);
      document.title = 'NinjAthlete | Biohacking, Peptide Therapy & Performance Optimization';
    };
  }, [product, reviewStats, reviews]);
  return null;
};

// Static page SEO helper - now includes canonical + OG + breadcrumbs + image
const usePageSEO = (title, description, path, image) => {
  const location = useLocation();
  useEffect(() => {
    const pagePath = path || location.pathname;
    document.title = title;
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', `${SITE_URL}${pagePath}`);
    if (image) {
      setMeta('property', 'og:image', image);
      setMeta('name', 'twitter:image', image);
    }
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setLink('canonical', `${SITE_URL}${pagePath}`);
    return () => { document.title = 'NinjAthlete | Biohacking, Peptide Therapy & Performance Optimization'; };
  }, [title, description, path, image, location.pathname]);
};

// Generic page-level SEO component (use on any page)
export const PageSEO = ({ title, description, path, image, breadcrumbs }) => {
  usePageSEO(title, description, path, image);
  useEffect(() => {
    if (!breadcrumbs || breadcrumbs.length === 0) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": b.name,
        "item": b.url.startsWith('http') ? b.url : `${SITE_URL}${b.url}`
      }))
    };
    const script = injectJsonLd('page-breadcrumb-jsonld', schema);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, [breadcrumbs]);
  return null;
};

export const SportsEventSchema = () => {
  usePageSEO(
    'HYROX Training Hub — Race Prep, Gym Finder & Equipment Guide | NinjAthlete',
    'Complete HYROX guide: training plans, gym finder, affiliate costs, equipment, and race strategies. Everything you need to train for and dominate HYROX.',
    '/hyrox'
  );
  return null;
};

export const CollectionPageSchema = ({ title, description }) => {
  usePageSEO(`${title} | NinjAthlete`, description || '');
  return null;
};

export const ContactPageSchema = () => {
  usePageSEO(
    'Contact NinjAthlete | Get In Touch',
    'Contact NinjAthlete for questions about biohacking, peptide therapy, HYROX training, and performance optimization.',
    '/contact'
  );
  return null;
};

export const AboutPageSchema = () => {
  usePageSEO(
    'About NinjAthlete | Biohacking & Performance Optimization',
    'Learn about NinjAthlete — our mission to bridge ancient warrior wisdom with modern bioscience for peak human performance.',
    '/about'
  );
  return null;
};

// Calculator SEO — HowTo + FAQPage JSON-LD via DOM injection
export const CalculatorJsonLd = ({ calculatorName, description, url, steps, faqs }) => {
  useEffect(() => {
    if (!calculatorName) return;

    const pageTitle = `${calculatorName} | NinjAthlete Tools`;
    document.title = pageTitle;

    const setMeta = (attr, attrVal, content) => {
      let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', calculatorName);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', url);

    const schemas = [];
    const injectedScripts = [];

    // HowTo schema
    if (steps && steps.length > 0) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": calculatorName,
        "description": description,
        "url": url,
        "step": steps.map((s, i) => ({
          "@type": "HowToStep",
          "position": i + 1,
          "name": s.name,
          "text": s.text,
        })),
        "tool": { "@type": "HowToTool", "name": "NinjAthlete Online Calculator" },
      });
    }

    // FAQPage schema
    if (faqs && faqs.length > 0) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({
          "@type": "Question",
          "name": f.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": f.answer,
          },
        })),
      });
    }

    schemas.forEach((schema, i) => {
      const id = `calc-schema-jsonld-${i}`;
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
      injectedScripts.push(script);
    });

    return () => {
      injectedScripts.forEach(s => { if (s.parentNode) s.parentNode.removeChild(s); });
      document.title = 'NinjAthlete | Biohacking, Peptide Therapy & Performance Optimization';
    };
  }, [calculatorName, description, url, steps, faqs]);

  return null;
};

// Generic FAQPage JSON-LD for non-article pages (Returns, Shipping, FAQ, etc.)
// `pagePath` is used as the WebPage @id so search engines can attach the FAQ to the page.
export const PageFAQSchema = ({ faqs, pageName, pagePath, scriptId = 'page-faq-schema' }) => {
  useEffect(() => {
    if (!faqs || faqs.length === 0) return;
    const pageUrl = `${SITE_URL}${pagePath || ''}`;
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "name": pageName,
      "url": pageUrl,
      "mainEntity": faqs.map(f => ({
        "@type": "Question",
        "name": f.question,
        "acceptedAnswer": { "@type": "Answer", "text": f.answer }
      }))
    };
    const script = injectJsonLd(scriptId, schema);
    return () => { if (script?.parentNode) script.parentNode.removeChild(script); };
  }, [faqs, pageName, pagePath, scriptId]);
  return null;
};

// Homepage FAQ Schema — JSON-LD
export const HomeFAQSchema = ({ faqs }) => {
  useEffect(() => {
    if (!faqs || faqs.length === 0) return;

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
    const faqScript = injectJsonLd('homepage-faq-schema', faqSchema);

    // Organization schema for homepage — enhanced with GEO entity markup
    const orgSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "NinjAthlete",
      "url": SITE_URL,
      "logo": `${SITE_URL}/logo.png`,
      "description": "Science-backed biohacking, peptide therapy protocols, HYROX training, and performance optimization. Evidence-based guides with cited research for athletes who refuse to settle.",
      "founder": {
        "@type": "Organization",
        "name": "NinjAthlete",
        "url": SITE_URL
      },
      "sameAs": [
        "https://instagram.com/ninjathlete",
        "https://twitter.com/ninjathlete",
        "https://www.tiktok.com/@ninjathlete",
        "https://www.youtube.com/@ninjathlete",
        "https://www.facebook.com/ninjathlete"
      ],
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "contactType": "customer support",
          "email": "hello@ninjathlete.com",
          "availableLanguage": ["English"],
          "areaServed": "US"
        },
        {
          "@type": "ContactPoint",
          "contactType": "returns",
          "email": "returns@ninjathlete.com",
          "url": `${SITE_URL}/returns`,
          "areaServed": "US"
        }
      ],
      "knowsAbout": ["HYROX Training Plans", "Peptide Therapy Protocols", "BPC-157", "AOD-9604", "Biohacking", "75 Hard Challenge", "Performance Nutrition", "Longevity Science"],
      "areaServed": { "@type": "Country", "name": "United States" },
      "address": { "@type": "PostalAddress", "addressLocality": "Dallas", "addressRegion": "TX", "addressCountry": "US" }
    };
    const orgScript = injectJsonLd('homepage-org-schema', orgSchema);

    // WebSite schema with SearchAction for sitelinks search box
    const siteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "NinjAthlete",
      "url": `${SITE_URL}/`,
      "description": "Evidence-based biohacking, peptide therapy, HYROX training, and longevity science for peak performance athletes.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": { "@type": "EntryPoint", "urlTemplate": `${SITE_URL}/blog?search={search_term_string}` },
        "query-input": "required name=search_term_string"
      }
    };
    const siteScript = injectJsonLd('homepage-site-schema', siteSchema);

    return () => {
      [faqScript, orgScript, siteScript].forEach(s => { if (s?.parentNode) s.parentNode.removeChild(s); });
    };
  }, [faqs]);
  return null;
};

// Product Catalog Schema for Shop page — ItemList with Google Shopping attributes
export const ProductCatalogSchema = ({ products }) => {
  useEffect(() => {
    if (!products || products.length === 0) return;

    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "NinjAthlete Shop — Performance Apparel & Gear",
      "description": "Science-backed performance apparel and gear for HYROX athletes, biohackers, and optimized performers.",
      "url": `${SITE_URL}/shop`,
      "numberOfItems": products.length,
      "itemListElement": products.slice(0, 50).map((p, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "item": {
          "@type": "Product",
          "name": p.name,
          "url": `${SITE_URL}/shop/${p.slug}`,
          "image": p.images?.[0] || '',
          "description": (p.seo_description || p.description || '').slice(0, 200),
          "sku": p.mpn || p.slug,
          "mpn": p.mpn || p.slug,
          "brand": { "@type": "Brand", "name": "NinjAthlete" },
          "itemCondition": "https://schema.org/NewCondition",
          ...(p.colors?.length ? { "color": p.colors.join(' / ') } : {}),
          ...(p.material ? { "material": p.material } : {}),
          "offers": {
            "@type": "Offer",
            "price": p.price || 0,
            "priceCurrency": "USD",
            "availability": (p.inventory_count > 0) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            "priceValidUntil": new Date(Date.now() + 90 * 24 * 3600 * 1000).toISOString().split('T')[0],
            "itemCondition": "https://schema.org/NewCondition",
          },
          ...(p.aggregate_rating ? {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": p.aggregate_rating.rating_value,
              "reviewCount": p.aggregate_rating.review_count,
            }
          } : {})
        }
      }))
    };
    const script = injectJsonLd('product-catalog-schema', schema);
    return () => { if (script?.parentNode) script.parentNode.removeChild(script); };
  }, [products]);
  return null;
};

export default { ArticleJsonLd, ProductSchema, ProductCatalogSchema, CollectionPageSchema, SportsEventSchema, ContactPageSchema, AboutPageSchema, CalculatorJsonLd, HomeFAQSchema, PageFAQSchema, PageSEO, useCanonicalUrl };
